import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
} from "@mui/material";
import { millisecondsToTimestamp } from "./CSV_Constants";
import { extractVimeoId } from "../../../network/vimeo_network_service/Vimeo_api_callback";
import { extractCloudflareStreamId } from "../../../network/cloudflare_stream_service/CloudflareStream_api_callback";

/** Resolve Cloudflare or Vimeo id from tambola/housie batch row data. */
const getHousieVideoIds = (data = {}) => {
    const rawCf = data?.cloudFlareVideoId != null ? String(data.cloudFlareVideoId).trim() : "";
    const rawUrl = data?.videoUrl != null ? String(data.videoUrl).trim() : "";

    const cfId =
        (rawCf && /^[a-fA-F0-9]{32}$/.test(rawCf) ? rawCf : null) ||
        extractCloudflareStreamId(rawCf) ||
        extractCloudflareStreamId(rawUrl) ||
        (rawUrl && /^[a-fA-F0-9]{32}$/.test(rawUrl) ? rawUrl : null) ||
        null;

    const vimeoId =
        (!cfId && extractVimeoId(rawUrl)) ||
        (!cfId && /^\d+$/.test(rawUrl) ? rawUrl : null) ||
        null;

    return { cfId, vimeoId, rawUrl, rawCf };
};

const BatchDetailsDialog = ({
    open,
    onClose,
    batchDetail,
    completedBatches = [],
    pendingBatches = [],
    onDownloadSuccess,
    onDownloadFailed,
}) => {

    function getEntityNamesString(data, unique = false) {
        if (!Array.isArray(data)) return "";

        // Extract entity names
        let names = data.map(item => item.entityName);

        // Remove duplicates if unique = true
        if (unique) {
            names = [...new Set(names)];
        }

        // Return comma-separated string
        return names.join(", ");
    }

    // ✅ Helper: render a styled status chip
    const renderStatusChip = (status) => {
        const isComplete = status?.toLowerCase() === "complete";
        const isPending = status?.toLowerCase() === "pending";
        return (
            <Chip
                label={status || "-"}
                color={isComplete ? "success" : isPending ? "warning" : "default"}
                size="small"
                sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    minWidth: 80,
                }}
            />
        );
    };

    // ✅ Render a table (Completed / Pending) - Entity
    const renderTableEntity = (title, data, color) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    background: color,
                    color: "#fff",
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                }}
            >
                {title} ({data.length})
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 400,
                    mt: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "auto",
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Entity Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            {/* <TableCell sx={{ fontWeight: 600 }}>Sub Category</TableCell> */}
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((item, index) => (
                                <TableRow key={item._id || index}>
                                    <TableCell>{item._id}</TableCell>
                                    <TableCell>{item?.data?.entityName || "-"}</TableCell>
                                    <TableCell>{item?.data?.entityCategory || "-"}</TableCell>
                                    {/* <TableCell>{item?.data?.entitySubCategory || "-"}</TableCell> */}
                                    <TableCell>{renderStatusChip(item?.status)}</TableCell>
                                    <TableCell>{item?.statusMsg || "-"}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // ✅ Render a table (Completed / Pending) - Fantasy
    const renderTableFantasy = (title, data, color) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    background: color,
                    color: "#fff",
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                }}
            >
                {title} ({data.length})
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 400,
                    mt: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "auto",
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>SubTitle</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Go Live At</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Submission CutOff At</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Pool Entity Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((item, index) => (
                                <TableRow key={item._id || index}>
                                    <TableCell>{item._id || "-"}</TableCell>
                                    <TableCell>{item?.data?.title || "-"}</TableCell>
                                    <TableCell>{item?.data?.subTitle || "-"}</TableCell>
                                    <TableCell>{millisecondsToTimestamp(item?.data?.goLiveAt) || "-"}</TableCell>
                                    <TableCell>{millisecondsToTimestamp(item?.data?.resultDeclarationAt) || "-"}</TableCell>
                                    <TableCell>{item?.data?.media?.length || "-"}</TableCell>
                                    <TableCell>{renderStatusChip(item?.status)}</TableCell>
                                    <TableCell>{item?.statusMsg || "-"}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // ✅ Render a table (Completed / Pending) - FantasyStats
    const renderTableFantasyStats = (title, data, color) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    background: color,
                    color: "#fff",
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                }}
            >
                {title} ({data.length})
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 400,
                    mt: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "auto",
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Box Office Collection</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Stream Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Rank/Position</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>View/Likes Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Comment Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Share Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Follower Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Upload Count</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((item, index) => (
                                <TableRow key={item._id || index}>
                                    <TableCell>{item._id || "-"}</TableCell>
                                    <TableCell>{item?.data?.name || "-"}</TableCell>
                                    <TableCell>{item?.data?.category || "-"}</TableCell>
                                    <TableCell>{item?.data?.collection || "-"}</TableCell>
                                    <TableCell>{item?.data?.count || "-"}</TableCell>
                                    <TableCell>{item?.data?.pos || "-"}</TableCell>
                                    <TableCell>{item?.data?.view || "-"}</TableCell>
                                    <TableCell>{item?.data?.comment || "-"}</TableCell>
                                    <TableCell>{item?.data?.share || "-"}</TableCell>
                                    <TableCell>{item?.data?.follower || "-"}</TableCell>
                                    <TableCell>{item?.data?.upload || "-"}</TableCell>
                                    <TableCell>{renderStatusChip(item?.status)}</TableCell>
                                    <TableCell>{item?.statusMsg || "-"}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // ✅ Render a table (Completed / Pending) - Rap Housie / Tambola Video
    const renderTableHousie = (title, data, color) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    background: color,
                    color: "#fff",
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                }}
            >
                {title} ({data.length})
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 480,
                    mt: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "auto",
                }}
            >
                <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Batch Row ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Provider</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 280, bgcolor: "#e3f2fd" }}>
                                Cloudflare Video ID
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Video ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 280 }}>Video URL</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Theme</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Timeline Count</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Timeline Entities</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((item, index) => {
                                const rowData = item?.data || {};
                                const { cfId, vimeoId, rawCf, rawUrl } = getHousieVideoIds(rowData);
                                // Prefer explicit API field first so ID always shows when present
                                const cloudflareDisplay =
                                    rowData?.cloudFlareVideoId ||
                                    cfId ||
                                    rawCf ||
                                    (!vimeoId && rawUrl && !String(rawUrl).includes("vimeo.com")
                                        ? extractCloudflareStreamId(rawUrl) || rawUrl
                                        : "") ||
                                    "-";
                                const provider = (cloudflareDisplay && cloudflareDisplay !== "-")
                                    ? "Cloudflare"
                                    : (vimeoId ? "Vimeo" : "-");
                                const entityNames = getEntityNamesString(rowData?.timeLine, false) || "-";
                                return (
                                    <TableRow key={item._id || index}>
                                        <TableCell sx={{ fontSize: 11 }}>{item._id || "-"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={provider}
                                                size="small"
                                                color={provider === "Cloudflare" ? "info" : provider === "Vimeo" ? "secondary" : "default"}
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: 13,
                                                fontWeight: 700,
                                                bgcolor: "#e3f2fd",
                                                color: "#0d47a1",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {cloudflareDisplay}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                            {vimeoId || "-"}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 11, maxWidth: 280, wordBreak: "break-all" }}>
                                            {rowData?.videoUrl || rawUrl || "-"}
                                        </TableCell>
                                        <TableCell>{rowData?.duration || "-"}</TableCell>
                                        <TableCell>{rowData?.theme || "-"}</TableCell>
                                        <TableCell>{rowData?.timeLine?.length || "-"}</TableCell>
                                        <TableCell
                                            title={entityNames}
                                            sx={{
                                                maxWidth: 220,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                fontSize: 11,
                                            }}
                                        >
                                            {entityNames}
                                        </TableCell>
                                        <TableCell>{renderStatusChip(item?.status)}</TableCell>
                                        <TableCell>{item?.statusMsg || "-"}</TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // ✅ Render a table (Completed / Pending) - FantasyStats
    const renderTableQuestionBank = (title, data, color) => (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    background: color,
                    color: "#fff",
                    px: 2,
                    py: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    borderRadius: 1,
                }}
            >
                {title} ({data.length})
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 400,
                    mt: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "auto",
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>CSV Content ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>CSV Question ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Game Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Entity Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Entity Type</TableCell>
                            {/* <TableCell sx={{ fontWeight: 600 }}>Entity Sub Type</TableCell> */}
                            <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data.map((item, index) => (
                                <TableRow key={item._id || index}>
                                    <TableCell>{item._id || "-"}</TableCell>
                                    <TableCell>{item?.data?.csvContentId || "-"}</TableCell>
                                    <TableCell>{item?.data?.csv_questionId || "-"}</TableCell>
                                    <TableCell>{Array.isArray(item?.data?.gameType)
                                        ? item.data.gameType.join(", ")
                                        : item?.data?.gameType || "-"}</TableCell>
                                    <TableCell>{item?.data?.entityName || "-"}</TableCell>
                                    <TableCell>{item?.data?.entityType || "-"}</TableCell>
                                    {/* <TableCell>{item?.data?.entitySubType || "-"}</TableCell> */}
                                    <TableCell>{item?.data?.difficulty || "-"}</TableCell>
                                    <TableCell>{renderStatusChip(item?.status)}</TableCell>
                                    <TableCell>{item?.statusMsg || "-"}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: { borderRadius: 2, overflow: "hidden" },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                Batch Details
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ p: 2, overflow: "auto" }}>
                {batchDetail?.type === 'tambolavideo' && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                            Rap Housie batch: {batchDetail?.batchName || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            Cloudflare Video ID:{" "}
                            <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700, color: "#0d47a1" }}>
                                {getHousieVideoIds(completedBatches?.[0]?.data || pendingBatches?.[0]?.data).cfId
                                    || completedBatches?.[0]?.data?.cloudFlareVideoId
                                    || pendingBatches?.[0]?.data?.cloudFlareVideoId
                                    || "-"}
                            </Box>
                        </Typography>
                    </Box>
                )}

                {batchDetail?.type === 'entity' && (
                    <>
                        {renderTableEntity("✅ Completed", completedBatches, "#2e7d32")}
                        {renderTableEntity("⚠️ Pending/Failed", pendingBatches, "#d32f2f")}
                    </>
                )}

                {batchDetail?.type === 'fantacy' && (
                    <>
                        {renderTableFantasy("✅ Completed", completedBatches, "#2e7d32")}
                        {renderTableFantasy("⚠️ Pending/Failed", pendingBatches, "#d32f2f")}
                    </>
                )}

                {batchDetail?.type === 'fantacystats' && (
                    <>
                        {renderTableFantasyStats("✅ Completed", completedBatches, "#2e7d32")}
                        {renderTableFantasyStats("⚠️ Pending/Failed", pendingBatches, "#d32f2f")}
                    </>
                )}

                {batchDetail?.type === 'tambolavideo' && (
                    <>
                        {renderTableHousie("✅ Completed", completedBatches, "#2e7d32")}
                        {renderTableHousie("⚠️ Pending/Failed", pendingBatches, "#d32f2f")}
                    </>
                )}

                {batchDetail?.type === 'question_bank' && (
                    <>
                        {renderTableQuestionBank("✅ Completed", completedBatches, "#2e7d32")}
                        {renderTableQuestionBank("⚠️ Pending/Failed", pendingBatches, "#d32f2f")}
                    </>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onDownloadSuccess}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Download Completed
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    onClick={onDownloadFailed}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Download Pending/Failed
                </Button>

                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchDetailsDialog;
